import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import {
  Text,
  Card,
  Button,
  useTheme,
  Chip,
  Avatar,
  Divider,
  ProgressBar,
} from 'react-native-paper';
import {
  MapPin,
  Clock,
  Calendar,
  Star,
  TrendingUp,
  BookOpen,
  Target,
  Users,
  MessageCircle,
  Globe,
  Zap,
  Award,
  Coffee,
  BarChart3,
} from 'lucide-react-native';
import type {
  PeerProfile,
  CompatibilityScore,
  PeerInteraction,
} from '../../store/slices/peerMatchingSlice';

interface PeerProfileDisplayProps {
  peer: PeerProfile;
  compatibility?: CompatibilityScore;
  interactions?: PeerInteraction[];
  isConnected?: boolean;
  onConnect?: () => void;
  onMessage?: () => void;
  onStartStudySession?: () => void;
  showCompatibilityIndicators?: boolean;
}

export function PeerProfileDisplay({
  peer,
  compatibility,
  interactions = [],
  isConnected = false,
  onConnect,
  onMessage,
  onStartStudySession,
  showCompatibilityIndicators = true,
}: PeerProfileDisplayProps) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<
    'overview' | 'compatibility' | 'activity'
  >('overview');

  // Format time zone display
  const formatTimeZone = (timeZone: string) => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
        hour: 'numeric',
        minute: '2-digit',
      });
      return formatter.format(now);
    } catch {
      return timeZone;
    }
  };

  // Calculate activity stats
  const getActivityStats = () => {
    const totalInteractions = interactions.length;
    const studySessions = interactions.filter(
      i => i.type === 'study_session'
    ).length;
    const averageRating =
      interactions
        .filter(i => i.rating)
        .reduce((sum, i) => sum + (i.rating || 0), 0) /
      (interactions.filter(i => i.rating).length || 1);

    const totalDuration = interactions
      .filter(i => i.duration)
      .reduce((sum, i) => sum + (i.duration || 0), 0);

    return {
      totalInteractions,
      studySessions,
      averageRating: Math.round(averageRating * 10) / 10,
      totalHours: Math.round(totalDuration / 60),
    };
  };

  const activityStats = getActivityStats();

  // Get compatibility color
  const getCompatibilityColor = (score: number) => {
    if (score >= 0.8) return '#4ECDC4';
    if (score >= 0.6) return '#FF6B35';
    return '#E74C3C';
  };

  const renderOverviewTab = () => (
    <View style={styles.tabContent}>
      {/* Basic Info */}
      <Card style={[styles.section, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
          >
            About
          </Text>

          {peer.bio && (
            <Text
              variant='bodyMedium'
              style={[styles.bio, { color: theme.colors.onSurfaceVariant }]}
            >
              {peer.bio}
            </Text>
          )}

          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <Globe size={16} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {formatTimeZone(peer.timeZone)}
              </Text>
            </View>

            <View style={styles.infoItem}>
              <Zap size={16} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Level {Math.round(peer.skillLevel * 10)}/10
              </Text>
            </View>

            <View style={styles.infoItem}>
              <Calendar size={16} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Joined {new Date(peer.joinedAt).toLocaleDateString()}
              </Text>
            </View>

            <View style={styles.infoItem}>
              <Clock size={16} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Active {peer.lastActive}
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Mastery Areas */}
      {peer.masteryAreas.length > 0 && (
        <Card
          style={[styles.section, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Expertise Areas
            </Text>
            <View style={styles.masteryGrid}>
              {peer.masteryAreas.map((area, index) => (
                <Chip
                  key={index}
                  mode='outlined'
                  textStyle={styles.masteryChipText}
                  style={[
                    styles.masteryChip,
                    { borderColor: theme.colors.primary },
                  ]}
                  icon={() => <Coffee size={12} color={theme.colors.primary} />}
                >
                  {area.replace(/_/g, ' ')}
                </Chip>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Learning Goals */}
      {peer.learningGoals.length > 0 && (
        <Card
          style={[styles.section, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Learning Goals
            </Text>
            <View style={styles.goalsContainer}>
              {peer.learningGoals.map((goal, index) => (
                <View key={index} style={styles.goalItem}>
                  <Target size={14} color={theme.colors.primary} />
                  <Text
                    variant='bodyMedium'
                    style={[styles.goalText, { color: theme.colors.onSurface }]}
                  >
                    {goal}
                  </Text>
                </View>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Availability */}
      {peer.availableHours.length > 0 && (
        <Card
          style={[styles.section, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Available Hours
            </Text>
            <View style={styles.availabilityGrid}>
              {peer.availableHours.map((hour, index) => (
                <Chip
                  key={index}
                  mode='outlined'
                  textStyle={styles.availabilityChipText}
                  style={[
                    styles.availabilityChip,
                    { borderColor: theme.colors.outline },
                  ]}
                >
                  {hour}
                </Chip>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}
    </View>
  );

  const renderCompatibilityTab = () => {
    if (!compatibility) {
      return (
        <View style={styles.tabContent}>
          <Text
            variant='bodyMedium'
            style={[
              styles.noDataText,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Compatibility data not available
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.tabContent}>
        {/* Overall Compatibility */}
        <Card
          style={[styles.section, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <View style={styles.compatibilityHeader}>
              <Text
                variant='titleMedium'
                style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
              >
                Overall Compatibility
              </Text>
              <Chip
                mode='filled'
                textStyle={[styles.compatibilityScore, { color: 'white' }]}
                style={[
                  styles.compatibilityChip,
                  {
                    backgroundColor: getCompatibilityColor(
                      compatibility.overall
                    ),
                  },
                ]}
              >
                {Math.round(compatibility.overall * 100)}%
              </Chip>
            </View>
          </Card.Content>
        </Card>

        {/* Detailed Breakdown */}
        <Card
          style={[styles.section, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Compatibility Breakdown
            </Text>

            <View style={styles.compatibilityItem}>
              <View style={styles.compatibilityLabel}>
                <Zap size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodyMedium'
                  style={{ color: theme.colors.onSurface }}
                >
                  Skill Level Alignment
                </Text>
              </View>
              <View style={styles.compatibilityScore}>
                <ProgressBar
                  progress={compatibility.skillAlignment}
                  color={getCompatibilityColor(compatibility.skillAlignment)}
                  style={styles.progressBar}
                />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  {Math.round(compatibility.skillAlignment * 100)}%
                </Text>
              </View>
            </View>

            <View style={styles.compatibilityItem}>
              <View style={styles.compatibilityLabel}>
                <Target size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodyMedium'
                  style={{ color: theme.colors.onSurface }}
                >
                  Learning Goals
                </Text>
              </View>
              <View style={styles.compatibilityScore}>
                <ProgressBar
                  progress={compatibility.goalAlignment}
                  color={getCompatibilityColor(compatibility.goalAlignment)}
                  style={styles.progressBar}
                />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  {Math.round(compatibility.goalAlignment * 100)}%
                </Text>
              </View>
            </View>

            <View style={styles.compatibilityItem}>
              <View style={styles.compatibilityLabel}>
                <BookOpen size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodyMedium'
                  style={{ color: theme.colors.onSurface }}
                >
                  Learning Style
                </Text>
              </View>
              <View style={styles.compatibilityScore}>
                <ProgressBar
                  progress={compatibility.styleAlignment}
                  color={getCompatibilityColor(compatibility.styleAlignment)}
                  style={styles.progressBar}
                />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  {Math.round(compatibility.styleAlignment * 100)}%
                </Text>
              </View>
            </View>

            <View style={styles.compatibilityItem}>
              <View style={styles.compatibilityLabel}>
                <Calendar size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodyMedium'
                  style={{ color: theme.colors.onSurface }}
                >
                  Schedule Compatibility
                </Text>
              </View>
              <View style={styles.compatibilityScore}>
                <ProgressBar
                  progress={compatibility.scheduleAlignment}
                  color={getCompatibilityColor(compatibility.scheduleAlignment)}
                  style={styles.progressBar}
                />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  {Math.round(compatibility.scheduleAlignment * 100)}%
                </Text>
              </View>
            </View>
          </Card.Content>
        </Card>

        {/* Common Areas */}
        {compatibility.breakdown.commonMasteryAreas.length > 0 && (
          <Card
            style={[styles.section, { backgroundColor: theme.colors.surface }]}
          >
            <Card.Content>
              <Text
                variant='titleMedium'
                style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
              >
                Common Interests
              </Text>
              <View style={styles.commonAreasGrid}>
                {compatibility.breakdown.commonMasteryAreas.map(
                  (area, index) => (
                    <Chip
                      key={index}
                      mode='filled'
                      textStyle={styles.commonAreaChipText}
                      style={[
                        styles.commonAreaChip,
                        { backgroundColor: theme.colors.primaryContainer },
                      ]}
                    >
                      {area.replace(/_/g, ' ')}
                    </Chip>
                  )
                )}
              </View>
            </Card.Content>
          </Card>
        )}
      </View>
    );
  };

  const renderActivityTab = () => (
    <View style={styles.tabContent}>
      {/* Activity Stats */}
      <Card style={[styles.section, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
          >
            Activity Statistics
          </Text>

          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <MessageCircle size={20} color={theme.colors.primary} />
              <Text
                variant='bodyLarge'
                style={[styles.statValue, { color: theme.colors.onSurface }]}
              >
                {activityStats.totalInteractions}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Interactions
              </Text>
            </View>

            <View style={styles.statItem}>
              <BookOpen size={20} color={theme.colors.primary} />
              <Text
                variant='bodyLarge'
                style={[styles.statValue, { color: theme.colors.onSurface }]}
              >
                {activityStats.studySessions}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Study Sessions
              </Text>
            </View>

            <View style={styles.statItem}>
              <Star size={20} color={theme.colors.primary} />
              <Text
                variant='bodyLarge'
                style={[styles.statValue, { color: theme.colors.onSurface }]}
              >
                {activityStats.averageRating}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Avg Rating
              </Text>
            </View>

            <View style={styles.statItem}>
              <Clock size={20} color={theme.colors.primary} />
              <Text
                variant='bodyLarge'
                style={[styles.statValue, { color: theme.colors.onSurface }]}
              >
                {activityStats.totalHours}h
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Total Time
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Recent Interactions */}
      {interactions.length > 0 && (
        <Card
          style={[styles.section, { backgroundColor: theme.colors.surface }]}
        >
          <Card.Content>
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Recent Activity
            </Text>

            {interactions.slice(0, 5).map((interaction, index) => (
              <View key={interaction.id}>
                <View style={styles.interactionItem}>
                  <View style={styles.interactionIcon}>
                    {interaction.type === 'study_session' && (
                      <BookOpen size={16} color={theme.colors.primary} />
                    )}
                    {interaction.type === 'message' && (
                      <MessageCircle size={16} color={theme.colors.primary} />
                    )}
                    {interaction.type === 'challenge' && (
                      <Award size={16} color={theme.colors.primary} />
                    )}
                    {interaction.type === 'collaboration' && (
                      <Users size={16} color={theme.colors.primary} />
                    )}
                  </View>

                  <View style={styles.interactionContent}>
                    <Text
                      variant='bodyMedium'
                      style={{ color: theme.colors.onSurface }}
                    >
                      {interaction.type
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase())}
                    </Text>
                    <Text
                      variant='bodySmall'
                      style={{ color: theme.colors.onSurfaceVariant }}
                    >
                      {new Date(interaction.timestamp).toLocaleDateString()}
                    </Text>
                  </View>

                  {interaction.rating && (
                    <View style={styles.interactionRating}>
                      <Star size={14} color='#FFD700' />
                      <Text
                        variant='bodySmall'
                        style={{ color: theme.colors.onSurfaceVariant }}
                      >
                        {interaction.rating}
                      </Text>
                    </View>
                  )}
                </View>

                {index < interactions.slice(0, 5).length - 1 && (
                  <Divider style={styles.interactionDivider} />
                )}
              </View>
            ))}
          </Card.Content>
        </Card>
      )}
    </View>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.colors.surface }]}>
        <View style={styles.profileHeader}>
          <Avatar.Image
            size={80}
            source={{ uri: peer.avatarUrl || 'https://via.placeholder.com/80' }}
            style={styles.avatar}
          />
          <View style={styles.headerInfo}>
            <View style={styles.nameRow}>
              <Text
                variant='headlineSmall'
                style={[styles.displayName, { color: theme.colors.onSurface }]}
              >
                {peer.displayName}
              </Text>
              {peer.isOnline && (
                <View
                  style={[
                    styles.onlineIndicator,
                    { backgroundColor: '#4ECDC4' },
                  ]}
                />
              )}
            </View>
            <Text
              variant='bodyMedium'
              style={[
                styles.username,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              @{peer.username}
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.preferredStyle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              {peer.preferredLearningStyle} learner
            </Text>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          {!isConnected && onConnect && (
            <Button
              mode='contained'
              onPress={onConnect}
              style={[styles.actionButton, styles.connectButton]}
              labelStyle={styles.connectButtonLabel}
              icon={Users}
            >
              Connect
            </Button>
          )}

          {isConnected && onMessage && (
            <Button
              mode='contained'
              onPress={onMessage}
              style={[styles.actionButton, styles.messageButton]}
              labelStyle={styles.messageButtonLabel}
              icon={MessageCircle}
            >
              Message
            </Button>
          )}

          {isConnected && onStartStudySession && (
            <Button
              mode='outlined'
              onPress={onStartStudySession}
              style={styles.actionButton}
              labelStyle={styles.actionButtonLabel}
              icon={BookOpen}
            >
              Study Together
            </Button>
          )}
        </View>
      </View>

      {/* Tabs */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'overview' && {
              borderBottomColor: theme.colors.primary,
            },
          ]}
          onPress={() => setActiveTab('overview')}
        >
          <Text
            variant='bodyMedium'
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'overview'
                    ? theme.colors.primary
                    : theme.colors.onSurfaceVariant,
                fontWeight: activeTab === 'overview' ? '600' : '400',
              },
            ]}
          >
            Overview
          </Text>
        </TouchableOpacity>

        {showCompatibilityIndicators && compatibility && (
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'compatibility' && {
                borderBottomColor: theme.colors.primary,
              },
            ]}
            onPress={() => setActiveTab('compatibility')}
          >
            <Text
              variant='bodyMedium'
              style={[
                styles.tabText,
                {
                  color:
                    activeTab === 'compatibility'
                      ? theme.colors.primary
                      : theme.colors.onSurfaceVariant,
                  fontWeight: activeTab === 'compatibility' ? '600' : '400',
                },
              ]}
            >
              Compatibility
            </Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'activity' && {
              borderBottomColor: theme.colors.primary,
            },
          ]}
          onPress={() => setActiveTab('activity')}
        >
          <Text
            variant='bodyMedium'
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'activity'
                    ? theme.colors.primary
                    : theme.colors.onSurfaceVariant,
                fontWeight: activeTab === 'activity' ? '600' : '400',
              },
            ]}
          >
            Activity
          </Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'compatibility' && renderCompatibilityTab()}
      {activeTab === 'activity' && renderActivityTab()}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 20,
    marginBottom: 8,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  avatar: {
    marginRight: 16,
  },
  headerInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  displayName: {
    fontWeight: '600',
  },
  onlineIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  username: {
    marginBottom: 2,
  },
  preferredStyle: {
    textTransform: 'capitalize',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  connectButton: {
    backgroundColor: '#4ECDC4',
  },
  connectButtonLabel: {
    color: 'white',
  },
  messageButton: {
    backgroundColor: '#4ECDC4',
  },
  messageButtonLabel: {
    color: 'white',
  },
  actionButtonLabel: {
    fontSize: 14,
  },
  tabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    marginBottom: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabText: {
    fontSize: 14,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  section: {
    marginBottom: 16,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  bio: {
    lineHeight: 20,
    marginBottom: 16,
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: '45%',
  },
  masteryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  masteryChip: {
    marginBottom: 4,
  },
  masteryChipText: {
    fontSize: 12,
    textTransform: 'capitalize',
  },
  goalsContainer: {
    gap: 8,
  },
  goalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  goalText: {
    flex: 1,
  },
  availabilityGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  availabilityChip: {
    marginBottom: 4,
  },
  availabilityChipText: {
    fontSize: 11,
  },
  compatibilityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  compatibilityChip: {
    height: 32,
  },
  compatibilityScore: {
    fontWeight: '600',
  },
  compatibilityItem: {
    marginBottom: 16,
  },
  compatibilityLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    flex: 1,
    marginRight: 12,
  },
  commonAreasGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  commonAreaChip: {
    marginBottom: 4,
  },
  commonAreaChipText: {
    fontSize: 12,
    textTransform: 'capitalize',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 16,
  },
  statItem: {
    alignItems: 'center',
    minWidth: '22%',
    gap: 4,
  },
  statValue: {
    fontWeight: '600',
    fontSize: 18,
  },
  interactionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  interactionIcon: {
    marginRight: 12,
  },
  interactionContent: {
    flex: 1,
  },
  interactionRating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  interactionDivider: {
    marginVertical: 4,
  },
  noDataText: {
    textAlign: 'center',
    fontStyle: 'italic',
    paddingVertical: 32,
  },
});
